import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { NavigationIds } from "components/v2/shared-navigation/SharedNavigation";
import { getActiveTab, getCurrentUserId, getNhDealApplicationId } from "features/authSlice";
import { selectIsUqualAdBannerDismissed, setUqualAdBannerDismissed } from "features/uqualAdBannerSlice";
import { useUqualReferralState } from "hooks/useUqualReferralState";
import { getUqualBannerDismissKey } from "utilities/Constant";

export const useUqualAdBannerVisibility = () => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const userId = useSelector(getCurrentUserId);
  const activeTab = useSelector(getActiveTab);
  const nhDealApplicationId = useSelector(getNhDealApplicationId);
  const bannerDismissed = useSelector(selectIsUqualAdBannerDismissed);

  const isOnDashboardHome = useMemo(() => {
    const onDashboardRoot = pathname === "/dashboard" || pathname === "/dashboard/";
    const isHomeTab = activeTab === "" || activeTab === NavigationIds.Home;
    return onDashboardRoot && isHomeTab;
  }, [pathname, activeTab]);

  const skipDashboardQuery = !isOnDashboardHome || !nhDealApplicationId || !userId;

  const { effectiveShowBanner } = useUqualReferralState({
    skipQuery: skipDashboardQuery,
  });

  useEffect(() => {
    if (!userId || !nhDealApplicationId) {
      return;
    }

    const dismissed = sessionStorage.getItem(getUqualBannerDismissKey(userId, nhDealApplicationId)) === "true";
    dispatch(setUqualAdBannerDismissed(dismissed));
  }, [dispatch, userId, nhDealApplicationId]);

  const isBannerVisible = useMemo(
    () => Boolean(nhDealApplicationId && userId && isOnDashboardHome && effectiveShowBanner && !bannerDismissed),
    [nhDealApplicationId, userId, isOnDashboardHome, effectiveShowBanner, bannerDismissed],
  );

  const dismissBanner = useCallback(() => {
    if (userId && nhDealApplicationId) {
      sessionStorage.setItem(getUqualBannerDismissKey(userId, nhDealApplicationId), "true");
    }
    dispatch(setUqualAdBannerDismissed(true));
  }, [dispatch, userId, nhDealApplicationId]);

  return { isBannerVisible, dismissBanner };
};
